import { useState } from 'react';
import { TimeControl } from '../types';
import { isValidCustomInput } from '../utils/timeControlUtils';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';

interface TimeControlConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeControl: TimeControl | null;
  setTimeControl: (tc: TimeControl | null) => void;
  title?: string;
}

const TIME_CONTROL_PRESETS: { label: string; value: TimeControl | null | 'custom' }[] = [
  { label: 'No time limit', value: null },
  { label: '5 min (default)', value: { minutes: 5,  increment: 0, incrementType: 'none' } },
  { label: '1 min',         value: { minutes: 1,  increment: 0, incrementType: 'none' } },
  { label: '3 min',         value: { minutes: 3,  increment: 0, incrementType: 'none' } },
  { label: '10 min',        value: { minutes: 10, increment: 0, incrementType: 'none' } },
  { label: '5+3 Fischer',   value: { minutes: 5,  increment: 3, incrementType: 'fischer' } },
  { label: '3+2 Fischer',   value: { minutes: 3,  increment: 2, incrementType: 'fischer' } },
  { label: '5+3 Bronstein', value: { minutes: 5,  increment: 3, incrementType: 'bronstein' } },
  { label: 'Custom',        value: 'custom' },
];

export const TimeControlConfigModal: React.FC<TimeControlConfigModalProps> = ({
  isOpen,
  onClose,
  timeControl,
  setTimeControl,
  title = 'Time Control',
}) => {
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customMinutes, setCustomMinutes] = useState(timeControl?.minutes ?? 5);
  const [customIncrement, setCustomIncrement] = useState(timeControl?.increment ?? 0);
  const [customIncrementType, setCustomIncrementType] = useState<'none' | 'fischer' | 'bronstein'>(
    timeControl?.incrementType ?? 'none'
  );

  const effectiveTimeControl: TimeControl | null = timeControl;

  const handlePresetSelect = (preset: TimeControl | null | 'custom') => {
    if (preset === 'custom') {
      setIsCustomMode(true);
    } else {
      setTimeControl(preset);
      setIsCustomMode(false);
      onClose();
    }
  };

  const handleCustomConfirm = () => {
    const mins = Math.max(1, Math.min(180, customMinutes));
    const inc = Math.max(0, Math.min(60, customIncrement));
    setTimeControl({ minutes: mins, increment: inc, incrementType: customIncrementType });
    setIsCustomMode(false);
    onClose();
  };

  const handleClose = () => {
    setIsCustomMode(false);
    onClose();
  };

  const isCustomValid = isValidCustomInput(customMinutes, customIncrement);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="md"
    >
      <div className="space-y-3">
        {!isCustomMode ? (
          <>
            {TIME_CONTROL_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handlePresetSelect(preset.value)}
                className={`w-full rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors ${
                  preset.value !== 'custom' && JSON.stringify(effectiveTimeControl) === JSON.stringify(preset.value)
                    ? 'border-cyan-300/70 bg-cyan-500/15 text-white'
                    : 'border-white/5 bg-[#0f172a]/40 text-slate-300 hover:bg-cyan-900/20'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">
                Minutes (1–180)
              </label>
              <Input
                type="number"
                value={customMinutes}
                onChange={(e) => setCustomMinutes(Math.max(1, Math.min(180, parseInt(e.target.value) || 1)))}
                min={1}
                max={180}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">
                Increment in seconds (0–60)
              </label>
              <Input
                type="number"
                value={customIncrement}
                onChange={(e) => setCustomIncrement(Math.max(0, Math.min(60, parseInt(e.target.value) || 0)))}
                min={0}
                max={60}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Increment type</label>
              <div className="space-y-2">
                {(['none', 'fischer', 'bronstein'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setCustomIncrementType(type)}
                    className={`w-full rounded-xl border px-4 py-2 text-left text-sm font-medium capitalize transition-colors ${
                      customIncrementType === type
                        ? 'border-cyan-300/70 bg-cyan-500/15 text-white'
                        : 'border-white/5 bg-[#0f172a]/40 text-slate-300 hover:bg-cyan-900/20'
                    }`}
                  >
                    {type === 'none' ? 'No increment' : type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setIsCustomMode(false)}>Back</Button>
              <Button
                onClick={handleCustomConfirm}
                disabled={!isCustomValid}
              >
                Confirm
              </Button>
            </div>
          </div>
        )}
        {!isCustomMode && (
          <div className="flex justify-end pt-2">
            <Button variant="secondary" onClick={handleClose}>Done</Button>
          </div>
        )}
      </div>
    </Modal>
  );
};